import { useEffect, useMemo, useRef } from 'react';
import { Toaster } from 'sonner';

import { ForecastModel } from '@app/common/static-data/models';
import { LoadingSpinner } from '@app/components/common/LoadingSpinner';

import { VariableId, Location } from './types';

import { ForecastDesktopLayout } from './components/ForecastDesktopLayout';
import { ForecastMobileLayout } from './components/ForecastMobileLayout';
import { useForecastData } from './hooks/useForecastData';
import { useForecastModels } from './hooks/useForecastModels';
import { useForecastPanel } from './hooks/useForecastPanel';
import { useForecastFavoritesWithWeatherPreferences } from './hooks/useForecastFavoritesWithWeatherPreferences';
import { useResponsive } from './hooks/useResponsive';
import { useWeatherPreferences } from './hooks/useWeatherPreferences';
import { useDemo } from '@app/components/demo/DemoProvider';
import { useForecastStateCache } from './hooks/useForecastStateCache';
import { useAvailableInitTimes } from './hooks/useAvailableInitTimes';

export default function Forecast() {
  const weatherPreferences = useWeatherPreferences();
  const stateCache = useForecastStateCache();
  const { isDemo } = useDemo();

  // Use cached state values
  const { selectedParam, endHour, location, isLoaded } = stateCache;

  // Sync location with weather preferences when cache is loaded
  useEffect(() => {
    if (!isLoaded) return;

    const syncLocationWithPreferences = async () => {
      try {
        // First, cleanup any duplicate cities
        await weatherPreferences.cleanupDuplicateCities();

        const preferences = await weatherPreferences.getWeatherPreferences();
        const cities = Object.values(preferences.favoriteCities);

        // If user has favorite cities and current location is not in favorites,
        // optionally update to first favorite (but don't override user's cached choice)
        if (cities.length > 0) {
          // Add current location to weather preferences if not already there
          await weatherPreferences.addOrUpdateCityIfNotExists(location);
        } else {
          // If no favorites exist, add current location as a favorite
          await weatherPreferences.addOrUpdateCityIfNotExists(location);
        }
      } catch (error) {
        console.error('Error syncing location with weather preferences:', error);
      }
    };

    syncLocationWithPreferences();
  }, [weatherPreferences, location, isLoaded]);

  // Save location to weather preferences when it changes
  useEffect(() => {
    if (!isLoaded) return;

    const saveToPreferences = async () => {
      try {
        await weatherPreferences.addOrUpdateCityIfNotExists(location);
      } catch (error) {
        console.error('Error saving location to weather preferences:', error);
      }
    };

    // Debounce the weather preferences save
    const timeoutId = setTimeout(saveToPreferences, 1000);
    return () => clearTimeout(timeoutId);
  }, [location, weatherPreferences, isLoaded]);

  // Custom hooks for different concerns
  const { isDesktop } = useResponsive();
  const panel = useForecastPanel();
  const favorites = useForecastFavoritesWithWeatherPreferences();

  // Destructure specific methods to avoid lint warnings
  const { isInitialLoadComplete, favoriteVariables, setFavoriteVariables } = favorites;

  // Get available models first
  const { availableModels } = useForecastData({
    location,
    selectedModels: [], // Empty to just get available models
    endHour,
    selectedInitTimes: {}, // Empty to just get available models
  });

  // Manage selected models with availability filtering
  const { selectedModels, setSelectedModels } = useForecastModels({
    availableModels,
    initialModels: stateCache.selectedModels,
  });

  // Get available init times for Jua models
  const {
    availableInitTimes,
    loadingInitTimes,
    openInitTimeSelectors,
    selectedInitTimes,
    handleInitTimeChange,
    handleInitTimeSelectorToggle,
  } = useAvailableInitTimes({ availableModels });

  // Determine when we should fetch forecasts (with stable memoization)
  const shouldFetchForecasts = useMemo(() => {
    if (!isLoaded || selectedModels.length === 0) {
      return false;
    }

    // Check if all Jua models have their init times set
    const juaModels = selectedModels.filter((modelId) =>
      availableModels.some((model) => model.id === modelId && model.provider === 'jua'),
    );

    // If we have Jua models, make sure all have init times set
    if (juaModels.length > 0) {
      return juaModels.every((modelId) => selectedInitTimes[modelId]);
    }

    return true; // No Jua models, safe to fetch
  }, [isLoaded, selectedModels, availableModels, selectedInitTimes]);

  // Memoize the models to pass to avoid unnecessary re-renders
  const modelsToFetch = useMemo(() => {
    return shouldFetchForecasts ? selectedModels : [];
  }, [shouldFetchForecasts, selectedModels]);

  // Single forecast data call
  const { forecastData, loading } = useForecastData({
    location,
    selectedModels: modelsToFetch,
    endHour,
    selectedInitTimes,
  });

  // Set default favorite variables for new users
  useEffect(() => {
    // Don't run until preferences have finished loading
    if (!isInitialLoadComplete) {
      return;
    }

    // Set default favorite variables (temperature and 10m wind) for new users
    if (favoriteVariables.size === 0) {
      const defaultVariables = new Set(['2t', '10si']); // Temperature and Wind Speed 10m
      setFavoriteVariables(defaultVariables);
    }
  }, [isInitialLoadComplete, favoriteVariables.size, setFavoriteVariables]);

  // Set default favorite models for demo users
  useEffect(() => {
    if (!isInitialLoadComplete) return;
    if (isDemo && favorites.favoriteModels.size === 0) {
      const defaultModels = new Set<string>(['ept1_5', 'ept2', 'ept2_e']);
      favorites.setFavoriteModels(defaultModels);
    }
  }, [isInitialLoadComplete, isDemo, favorites.favoriteModels.size, favorites.setFavoriteModels]);

  // Enhanced handlers with auto-favoriting for new users
  const handleParamChangeWithAutoFavorite = (param: VariableId) => {
    stateCache.setSelectedParam(param);

    // Auto-favorite if user has no favorite variables yet
    if (favorites.isInitialLoadComplete && favorites.favoriteVariables.size === 0) {
      const newFavorites = new Set(favorites.favoriteVariables);
      newFavorites.add(param);
      favorites.setFavoriteVariables(newFavorites);
    }
  };

  const handleLocationChangeWithAutoFavorite = (newLocation: Location) => {
    stateCache.setLocation(newLocation);

    // Auto-favorite if user has no favorite locations yet
    if (favorites.isInitialLoadComplete && favorites.favoriteLocations.length === 0) {
      favorites.addFavoriteLocation(newLocation);
    }
  };

  const handleModelsChangeWithAutoFavorite = (models: ForecastModel[]) => {
    setSelectedModels(models);
    stateCache.setSelectedModels(models);

    // Auto-favorite if user has no favorite models yet and is adding their first model
    if (
      favorites.isInitialLoadComplete &&
      favorites.favoriteModels.size === 0 &&
      models.length > 0
    ) {
      // Find the newly added model (the first one if starting from empty, or the difference)
      const newModel = models[models.length - 1]; // Take the last added model
      const newFavorites = new Set(favorites.favoriteModels);
      newFavorites.add(newModel);
      favorites.setFavoriteModels(newFavorites);
    }
  };

  // Enhanced init time change handler that auto-selects the model
  const handleInitTimeChangeAndSelectModel = (modelId: string, initTime: string) => {
    // Call the original init time change handler
    handleInitTimeChange(modelId, initTime);

    // Auto-select the model if it's not already selected
    if (!selectedModels.includes(modelId as ForecastModel)) {
      const newSelectedModels = [...selectedModels, modelId as ForecastModel];
      handleModelsChangeWithAutoFavorite(newSelectedModels);
    }
  };

  // In demo mode, seed EPT models once on initial load if none are selected
  const demoAutoSelectedRef = useRef(false);
  useEffect(() => {
    if (
      !isLoaded ||
      !isDemo ||
      demoAutoSelectedRef.current ||
      selectedModels.length > 0 ||
      availableModels.length === 0
    )
      return;

    const desiredIds: ForecastModel[] = ['ept1_5', 'ept2', 'ept2_e'];
    const availableDesired = desiredIds.filter((id) => availableModels.some((m) => m.id === id));

    if (availableDesired.length === 0) return; // try again when models load

    demoAutoSelectedRef.current = true;
    setSelectedModels(availableDesired);
    stateCache.setSelectedModels(availableDesired);
  }, [isLoaded, isDemo, availableModels, selectedModels.length, setSelectedModels, stateCache]);

  // Common props for both layouts
  const commonProps = {
    // Data props
    loading,
    forecastData,
    selectedParam,
    selectedModels,
    endHour,
    location,
    availableModels,

    // Favorites props
    favoriteVariables: favorites.favoriteVariables,
    favoriteModels: favorites.favoriteModels,
    favoriteLocations: favorites.favoriteLocations,
    onFavoriteVariablesChange: favorites.setFavoriteVariables,
    onFavoriteModelsChange: favorites.setFavoriteModels,
    addFavoriteLocation: favorites.addFavoriteLocation,
    removeFavoriteLocation: favorites.removeFavoriteLocation,
    isFavoriteLocation: favorites.isFavoriteLocation,

    // Model colors
    customModelColors: favorites.customModelColors,
    onModelColorChange: favorites.setModelColor,

    // Available Initialization times
    availableInitTimes,
    loadingInitTimes,
    openInitTimeSelectors,
    selectedInitTimes,
    onInitTimeChange: handleInitTimeChangeAndSelectModel,
    onInitTimeSelectorToggle: handleInitTimeSelectorToggle,

    // Enhanced handler props with auto-favoriting
    onParamChange: handleParamChangeWithAutoFavorite,
    onLocationChange: handleLocationChangeWithAutoFavorite,
    onModelsChange: handleModelsChangeWithAutoFavorite,
    onEndHourChange: stateCache.setEndHour,

    // Panel handlers
    handlePanelToggle: panel.handlePanelToggle,
    handleOpenPanelWithVariableExpansion: panel.handleOpenPanelWithVariableExpansion,
    handleOpenPanelWithLocationExpansion: panel.handleOpenPanelWithLocationExpansion,
    handleOpenPanelWithModelExpansion: panel.handleOpenPanelWithModelExpansion,
    handlePanelClose: panel.handlePanelClose,
  };

  // Don't render until cache is loaded to prevent flash of default state
  if (!isLoaded) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {isDesktop ? (
        <ForecastDesktopLayout
          {...commonProps}
          // Desktop-specific panel props
          isPanelOpen={panel.isPanelOpen}
          onSettingsClick={panel.handlePanelToggle}
          panelWidth={panel.panelWidth}
          isResizing={panel.isResizing}
          expandedVariableSection={panel.expandedVariableSection}
          expandedLocationSection={panel.expandedLocationSection}
          expandedModelSection={panel.expandedModelSection}
          expandedTimeRangeSection={panel.expandedTimeRangeSection}
          setPanelWidth={panel.setPanelWidth}
          setIsResizing={panel.setIsResizing}
          setExpandedVariableSection={panel.setExpandedVariableSection}
          setExpandedLocationSection={panel.setExpandedLocationSection}
          setExpandedModelSection={panel.setExpandedModelSection}
          setExpandedTimeRangeSection={panel.setExpandedTimeRangeSection}
          handlePanelToggle={panel.handlePanelToggle}
          handleOpenPanelWithVariableExpansion={panel.handleOpenPanelWithVariableExpansion}
          handleOpenPanelWithLocationExpansion={panel.handleOpenPanelWithLocationExpansion}
          handleOpenPanelWithModelExpansion={panel.handleOpenPanelWithModelExpansion}
          handlePanelClose={panel.handlePanelClose}
        />
      ) : (
        <ForecastMobileLayout
          {...commonProps}
          // Mobile-specific panel props
          isPanelOpen={panel.isPanelOpen}
          onSettingsClick={panel.handlePanelToggle}
          expandedVariableSection={panel.expandedVariableSection}
          expandedLocationSection={panel.expandedLocationSection}
          expandedModelSection={panel.expandedModelSection}
          expandedTimeRangeSection={panel.expandedTimeRangeSection}
          setExpandedVariableSection={panel.setExpandedVariableSection}
          setExpandedLocationSection={panel.setExpandedLocationSection}
          setExpandedModelSection={panel.setExpandedModelSection}
          setExpandedTimeRangeSection={panel.setExpandedTimeRangeSection}
        />
      )}
      <Toaster />
    </>
  );
}
